import React, { useEffect, useRef, useState } from "react";

const WA_NUMBER = "5491124289800";
const WA_EXTRAS = encodeURIComponent(
  "Hola! Me interesa consultar sobre los servicios extra disponibles."
);

type ExtraItem = {
  icon: string;
  label: string;
  image?: string;
};

const extras: ExtraItem[] = [
  {
    icon: "🎂",
    label: "Pastelería personalizada",
    image: "/images/extra/pasteleria.webp",
  },
  {
    icon: "💄",
    label: "Maquillaje artístico",
    image: "/images/extra/maquillajeArtistico.webp",
  },
  {
    icon: "🌟",
    label: "Maquillaje flúor",
    image: "/images/extra/maquillajeFluor.webp",
  },
  {
    icon: "💇",
    label: "Maquillaje + pelos locos",
    image: "/images/extra/pelosLocosyMaquillaje.webp",
  },
  {
    icon: "✨",
    label: "Glitter tattoo",
    image: "/images/extra/tatuajes.webp",
  },
  { icon: "💫", label: "Glitter bar", image: "/images/extra/glitterBar.jpeg" },
  {
    icon: "🔮",
    label: "Maquillaje neón",
    image: "/images/extra/glitterNeon.webp",
  },
  {
    icon: "🤖",
    label: "Show robot LED",
    image: "/images/extra/robot.webp",
  },
  {
    icon: "📸",
    label: "Cabina de fotos",
    image: "/images/extra/cabinaFotos.webp",
  },
  { icon: "🪄", label: "Espejo mágico" },
  { icon: "🎡", label: "Plataforma 360" },
  {
    icon: "🎩",
    label: "Show de magia",
    image: "/images/extra/magia.webp",
  },
  {
    icon: "🍿",
    label: "Stand pochoclos y copos",
    image: "/images/extra/standPochoclos.webp",
  },
  {
    icon: "🦸",
    label: "Personajes",
    image: "/images/extra/personajes.webp",
  },
  {
    icon: "📷",
    label: "Fotógrafo",
    image: "/images/extra/fotografia.webp",
  },
  { icon: "⏰", label: "Hora adicional" },
  {
    icon: "🍭",
    label: "Candy bar",
    image: "/images/extra/standGolosinas.webp",
  },
];

const firstExtraWithImage = extras.find((extra) => extra.image) ?? extras[0];

export default function Extras() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selectedExtra, setSelectedExtra] = useState<ExtraItem>(firstExtraWithImage);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el) => {
              el.classList.add("visible");
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isPreviewOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPreviewOpen]);

  const openPreview = (extra: ExtraItem) => {
    if (!extra.image) return;

    setSelectedExtra(extra);
    setIsPreviewOpen(true);
  };

  return (
    <>
      <section
        id="extras"
        ref={sectionRef}
        className="py-24 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #F6F3EA 0%, #eef7f1 100%)" }}
        aria-labelledby="extras-title"
      >
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16 reveal">
            <p className="section-subtitle mb-3">Personalizá tu festejo</p>
            <h2 id="extras-title" className="section-title mb-4">
              Servicios extra
            </h2>
            <p className="font-body text-text/70 text-lg max-w-2xl mx-auto">
              Tocá los servicios destacados para ver una imagen de ejemplo y elegir la combinación ideal para tu evento.
            </p>
          </div>

          <div className="reveal">
            <ul
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
              role="list"
              aria-label="Lista de servicios extra disponibles"
            >
              {extras.map((extra) => {
                const hasImage = Boolean(extra.image);

                return (
                  <li key={extra.label} className="h-full">
                    {hasImage ? (
                      <button
                        type="button"
                        onClick={() => openPreview(extra)}
                        className="group flex h-full min-h-[132px] w-full flex-col items-center justify-between gap-2 rounded-2xl border border-accent/20 bg-surface p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-green"
                        aria-label={`Ver imagen de ejemplo de ${extra.label}`}
                      >
                        <span
                          className="text-3xl group-hover:scale-110 transition-transform duration-300"
                          role="img"
                          aria-hidden="true"
                        >
                          {extra.icon}
                        </span>
                        <span className="font-body text-text text-xs font-medium leading-tight">
                          {extra.label}
                        </span>
                        <span className="font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/80">
                          Ver foto
                        </span>
                      </button>
                    ) : (
                      <div className="group flex h-full min-h-[132px] flex-col items-center justify-between gap-2 rounded-2xl border border-accent/20 bg-surface p-4 text-center opacity-95">
                        <span
                          className="text-3xl group-hover:scale-110 transition-transform duration-300"
                          role="img"
                          aria-label={extra.label}
                        >
                          {extra.icon}
                        </span>
                        <span className="font-body text-text text-xs font-medium leading-tight">
                          {extra.label}
                        </span>
                        <span className="font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-text/35">
                          Sin foto disponible
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="text-center mt-12 reveal">
            <p className="font-body text-text/70 mb-5">
              ¿Querés combinar servicios o tenés alguna idea especial? ¡Contanos!
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_EXTRAS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white font-body font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-green"
              aria-label="Consultar sobre servicios extra por WhatsApp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Consultá los extras disponibles
            </a>
          </div>
        </div>
      </section>

      {isPreviewOpen && selectedExtra.image && (
        <div
          className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 p-4 pt-8 sm:p-6 sm:pt-10 lg:pt-12"
          onClick={() => setIsPreviewOpen(false)}
          aria-hidden="true"
        >
          <div
            className="relative w-full max-w-6xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="extra-preview-title"
          >
            <button
              type="button"
              onClick={() => setIsPreviewOpen(false)}
              className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-text shadow-md transition hover:bg-white"
              aria-label="Cerrar vista previa"
            >
              ✕
            </button>

            <div className="grid max-h-[92vh] overflow-y-auto lg:grid-cols-[1.55fr_0.75fr]">
              <div className="relative min-h-[420px] bg-[#f3efe4] sm:min-h-[540px] lg:min-h-[78vh]">
                <img
                  src={selectedExtra.image}
                  alt={`Ejemplo de ${selectedExtra.label}`}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>

              <div className="flex flex-col justify-center p-6 text-center lg:p-10 lg:text-left">
                <p className="section-subtitle mb-3">Vista previa</p>
                <h3
                  id="extra-preview-title"
                  className="font-display text-3xl sm:text-4xl text-text mb-4"
                >
                  {selectedExtra.label}
                </h3>
                <p className="font-body text-text/70 text-base sm:text-lg mb-6">
                  Esta imagen ayuda a mostrar el estilo del servicio. Si querés sumar este extra o armar una propuesta combinada, lo vemos por WhatsApp.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap justify-center lg:justify-start">
                  <a
                    href={`https://wa.me/${WA_NUMBER}?text=${WA_EXTRAS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-secondary text-white font-body font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-green"
                    aria-label={`Consultar por ${selectedExtra.label} en WhatsApp`}
                  >
                    Consultar este extra
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
